import React, { useEffect, useState } from "react";
import useChats from "../../services/chat/useChats";
import useUser from "../../services/Settings/useUser";
import { toast, ToastContainer } from "react-toastify";
import { Pencil, Plus, RotateCcw, Trash } from "lucide-react";
import LoadingScreen from "../../utils/LoadingScreen";

function UserManagement() {
  const { people, setPeople, getAllUserList } = useChats();

  const { roles, inviteUser, getUserRoles, changeUserStatus, deleteUser } =
    useUser();

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [activeStatus, setActiveStatus] = useState(true);
  const [partialAccess, setPartialAccess] = useState(false);
  const [fullAccess, setFullAccess] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  const [inviteUserName, setInviteUserName] = useState("");
  const [inviteUserEmail, setInviteUserEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadUsers = async () => {
    try {
      setLoading(true);
      await getAllUserList();
      await getUserRoles();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  return (
    <>
    <ToastContainer />
  
    <div className="p-6 montserrat-font-1">
      {/* Card */}
      <div className="bg-white rounded-xl shadow border montserrat-font-1">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b v">
          <h2 className="text-lg font-semibold text-gray-700">Invite People</h2>

          <div className="flex gap-2">
            <button
              onClick={loadUsers}
              className="border px-3 py-2 rounded-lg text-white text-sm bg-blue-500 items-center flex gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Refresh
            </button>

            <button
              onClick={() => setInviteModalOpen(true)}
              className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              <Plus size={16} />
              Invite
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto montserrat-font-1">
          <table className="w-full text-sm bg-white">
            <thead className="bg-green-100 text-gray-800">
              <tr>
                <th className="px-6 py-3 text-left">Person Name</th>
                <th className="px-6 py-3 text-left">Email</th>
                <th className="px-6 py-3 text-left">Mobile No.</th>
                <th className="px-6 py-3 text-left">Status</th>
                <th className="px-6 py-3 text-left">Access</th>
                <th className="px-6 py-3 text-center">Action</th>
              </tr>
            </thead>

            <tbody className="divide-y montserrat-font-1">
              {loading ? (
                <tr>
                  <td colSpan="6" className="py-10">
                    <div className="flex justify-center">
                      <LoadingScreen text="Loading..." />
                    </div>
                  </td>
                </tr>
              ) : people?.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-6 text-gray-400">
                    No Users Found
                  </td>
                </tr>
              ) : (
                people?.map((item, index) => (
                  <tr key={index} className="hover:bg-green-50">
                    <td className="px-6 py-4 font-medium text-gray-700">
                      {item.user_name}
                    </td>

                    <td className="px-6 py-4 text-gray-600">
                      {item.user_email}
                    </td>

                    <td className="px-6 py-4 text-gray-600">
                      {item.user_mobile}
                    </td>

                    <td className="px-6 py-4">
                      <span
                        className={`px-3 py-1 text-xs rounded-full font-medium ${
                          item.user_status === "ACTIVE"
                            ? "bg-green-100 text-green-600"
                            : "bg-red-100 text-red-600"
                        }`}
                      >
                        {item.user_status === "ACTIVE" ? "Active" : "Inactive"}
                      </span>
                    </td>

                    <td className=" align-middle text-center text-order montserrat-font-1">
                      {item.control === "full" ? (
                        <>Full Control</>
                      ) : (
                        <>Partial Control</>
                      )}
                    </td>

                    <td className="px-6 py-4 flex justify-center gap-3">
                      {/* Edit */}
                      <button
                        onClick={() => {
                          setSelectedUser(item);
                          setActiveStatus(item.user_status === "ACTIVE");
                          setEditModalOpen(true);
                        }}
                        className="p-2 rounded-lg bg-green-50 hover:bg-green-100"
                      >
                        <Pencil size={16} />
                      </button>

                      {/* Delete */}
                      <button
                        onClick={() => {
                          setSelectedUser(item);
                          setSelectedUserId(item.userid);
                          setDeleteModalOpen(true);
                        }}
                        className="p-2 rounded-lg bg-red-50 hover:bg-red-100"
                      >
                        <Trash size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* edit user Access */}

          {editModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 montserrat-font-1">
              <div className="bg-white w-[480px] rounded-xl shadow-lg montserrat-font-1">
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b montserrat-font-1">
                  <h2 className="text-lg font-semibold text-gray-700">
                    Edit user Access
                  </h2>

                  <button
                    onClick={() => setEditModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-5 montserrat-font-1">
                  <p className="text-gray-600">
                    Manage Access for{" "}
                    <span className="font-semibold">
                      {selectedUser?.user_name}
                    </span>
                  </p>

                  {/* Active Status */}
                  <div className="flex justify-between items-center montserrat-font-1">
                    <span className="text-gray-700">Active Status</span>

                    <button
                      onClick={() => setActiveStatus(!activeStatus)}
                      className={`w-12 h-6 flex items-center rounded-full p-1 transition ${
                        activeStatus ? "bg-green-600" : "bg-gray-300"
                      }`}
                    >
                      <div
                        className={`bg-white w-4 h-4 rounded-full shadow transform transition ${
                          activeStatus ? "translate-x-6" : ""
                        }`}
                      />
                    </button>
                  </div>

                  {/* Role */}
                  <div className="montserrat-font-1">
                    <label className="text-gray-700 text-sm">Change Role</label>

                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="mt-1 w-full border rounded-lg px-3 py-2"
                    >
                      {roles?.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.role_title}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Partial Access */}
                  <div className="montserrat-font-1">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Partial Access</span>

                      <button
                        onClick={() => {
                          setPartialAccess(!partialAccess);
                          setFullAccess(false);
                        }}
                        className={`w-12 h-6 flex items-center rounded-full p-1 ${
                          partialAccess ? "bg-green-600" : "bg-gray-300"
                        }`}
                      >
                        <div
                          className={`bg-white w-4 h-4 rounded-full shadow transform ${
                            partialAccess ? "translate-x-6" : ""
                          }`}
                        />
                      </button>
                    </div>

                    <p className="text-xs text-gray-500 mt-1">
                      Partial control access typically grants limited
                      permissions, allowing users to perform specific tasks
                      within defined parameters.
                    </p>
                  </div>

                  {/* Full Access */}
                  <div className="montserrat-font-1">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Full Access</span>

                      <button
                        onClick={() => {
                          setFullAccess(!fullAccess);
                          setPartialAccess(false);
                        }}
                        className={`w-12 h-6 flex items-center rounded-full p-1 ${
                          fullAccess ? "bg-green-600" : "bg-gray-300"
                        }`}
                      >
                        <div
                          className={`bg-white w-4 h-4 rounded-full shadow transform ${
                            fullAccess ? "translate-x-6" : ""
                          }`}
                        />
                      </button>
                    </div>

                    <p className="text-xs text-gray-500 mt-1">
                      Full control access provides unrestricted authority,
                      enabling users to modify, create, and delete without
                      limitations.
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-6 py-4 border-t montserrat-font-1">
                  <button
                    onClick={() => setEditModalOpen(false)}
                    className="px-6 py-2 border border-green-600 text-green-600 rounded-lg"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={async () => {
                      setEditModalOpen(false);
                      const payload = {
                        userid: selectedUser.userid,
                        status: activeStatus ? "Y" : "N",
                      };

                      const res = await changeUserStatus(payload);

                      if (res?.responseCode === "00") {
                        toast.success(res.responseMessage);

                        setPeople((prev) =>
                          prev.map((user) =>
                            user.userid === selectedUser.userid
                              ? {
                                  ...user,
                                  user_status: activeStatus
                                    ? "ACTIVE"
                                    : "INACTIVE",
                                }
                              : user,
                          ),
                        );

                        setEditModalOpen(false);
                      }
                    }}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* invite user */}

          {inviteModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-4 montserrat-font-1">
              <div className="bg-white w-full max-w-[520px] rounded-xl shadow-lg flex flex-col max-h-[90vh] montserrat-font-1">
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b flex-shrink-0 montserrat-font-1">
                  <h2 className="text-lg font-semibold text-gray-700">
                    Invite People
                  </h2>
                  <button
                    onClick={() => setInviteModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </div>

                {/* Body — scrollable */}
                <div className="p-6 space-y-5 overflow-y-auto flex-1 montserrat-font-1">
                  <p className="text-gray-600 text-sm font-semibold montserrat-font-2">
                    Invite new members to your business by entering their email
                    or phone number. Customize their access permissions and
                    select the Accounts and tools they can use.
                  </p>

                  {/* Username */}
                  <div>
                    <label className="text-sm text-green-700">UserName</label>
                    <input
                      type="text"
                      value={inviteUserName}
                      onChange={(e) => setInviteUserName(e.target.value)}
                      className="mt-1 w-full border rounded-lg px-3 py-2"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="text-sm text-green-700">Email</label>
                    <input
                      type="email"
                      value={inviteUserEmail}
                      onChange={(e) => setInviteUserEmail(e.target.value)}
                      className="mt-1 w-full border rounded-lg px-3 py-2"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="text-sm text-green-700">
                      Phone Number
                    </label>
                    <input
                      type="text"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      className="mt-1 w-full border rounded-lg px-3 py-2"
                    />
                  </div>

                  {/* Role */}
                  <div>
                    <p className="text-gray-800 text-sm font-semibold mb-2 montserrat-font-2">
                      Choose the tasks that needs to perform within the Chaafo
                      business portfolio.
                    </p>
                    <label className="text-sm text-green-700">
                      Select Role
                    </label>

                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="mt-1 w-full border rounded-lg px-3 py-2"
                    >
                      {roles?.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.role_title}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Partial Access */}
                  <div>
                    <div className="flex justify-between items-center montserrat-font-1">
                      <span className="text-green-700">Partial Access</span>

                      <button
                        onClick={() => {
                          setPartialAccess(!partialAccess);
                          setFullAccess(false);
                        }}
                        className={`w-12 h-6 flex items-center rounded-full p-1 ${
                          partialAccess ? "bg-green-600" : "bg-gray-300"
                        }`}
                      >
                        <div
                          className={`bg-white w-4 h-4 rounded-full shadow transform ${
                            partialAccess ? "translate-x-6" : ""
                          }`}
                        />
                      </button>
                    </div>

                    <p className="text-sm text-gray-500 mt-1 montserrat-font-2">
                      Partial control access typically grants limited
                      permissions, allowing users to perform specific tasks
                      within defined parameters.
                    </p>
                  </div>

                  {/* Full Access */}
                  <div className="montserrat-font-1">
                    <div className="flex justify-between items-center">
                      <span className="text-green-700">Full Access</span>

                      <button
                        onClick={() => {
                          setFullAccess(!fullAccess);
                          setPartialAccess(false);
                        }}
                        className={`w-12 h-6 flex items-center rounded-full p-1 ${
                          fullAccess ? "bg-green-600" : "bg-gray-300"
                        }`}
                      >
                        <div
                          className={`bg-white w-4 h-4 rounded-full shadow transform ${
                            fullAccess ? "translate-x-6" : ""
                          }`}
                        />
                      </button>
                    </div>

                    <p className="text-sm text-gray-500 mt-1 montserrat-font-2">
                      Full control access provides unrestricted authority,
                      enabling users to modify, create, and delete without
                      limitations.
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-6 py-4 border-t flex-shrink-0 montserrat-font-1">
                  <button
                    onClick={() => setInviteModalOpen(false)}
                    className="px-6 py-2 border border-green-600 text-green-600 rounded-lg"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={async () => {
                      const payload = {
                        username: inviteUserName,
                        email: inviteUserEmail,
                        mobile: phoneNumber,
                        role: selectedRole,
                        access: fullAccess ? "FULL" : "PARTIAL",
                      };

                      const res = await inviteUser(payload);

                      if (res?.responseCode === "00") {
                        toast.success(res.responseMessage);

                        const newUser = {
                          userid: Date.now(),
                          user_name: inviteUserName,
                          user_email: inviteUserEmail,
                          user_mobile: phoneNumber,
                          user_status: "ACTIVE",
                          control: fullAccess ? "full" : "partial",
                        };

                        setPeople((prev) => [newUser, ...prev]);

                        setInviteModalOpen(false);

                        // clear fields
                        setInviteUserName("");
                        setInviteUserEmail("");
                        setPhoneNumber("");
                        setSelectedRole("");
                      }
                    }}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* delete model */}

          {deleteModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 montserrat-font-1">
              <div className="bg-white w-[400px] rounded-xl shadow-lg montserrat-font-1">
                {/* Header */}
                <div className="px-6 py-4 border-b">
                  <h2 className="text-lg font-semibold text-gray-700">
                    Delete User
                  </h2>
                </div>

                {/* Body */}
                <div className="p-6 space-y-3 text-gray-600 montserrat-font-1">
                  <p>Are you sure you want to delete this user?</p>

                  <div className="bg-gray-50 rounded-lg p-3 border text-sm space-y-1 montserrat-font-2">
                    <p>
                      <span className="font-semibold">Name:</span>{" "}
                      {selectedUser?.user_name}
                    </p>
                    <p>
                      <span className="font-semibold">Email:</span>{" "}
                      {selectedUser?.user_email}
                    </p>
                    <p>
                      <span className="font-semibold">Mobile:</span>{" "}
                      {selectedUser?.user_mobile}
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-6 py-4 border-t montserrat-font-1">
                  <button
                    onClick={() => setDeleteModalOpen(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={async () => {
                      const res = await deleteUser({
                        userid: selectedUserId,
                      });

                      if (res?.responseCode === "00") {
                        toast.success(res.responseMessage);

                        setPeople((prev) =>
                          prev.filter((user) => user.userid !== selectedUserId),
                        );
                      }

                      setDeleteModalOpen(false);
                    }}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

export default UserManagement;
